import { PAYMENT_METHODS, type PaymentMethod, type StructuredTerms } from '@loot/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Field, Input, Select } from '../ui.tsx';

/**
 * Condição de pagamento estruturada (ADR 0104): forma, prazo em dias, parcelas mensais iguais e dia
 * fixo opcional, os mesmos campos do padrão do fornecedor. Usada na proposta da cotação e no
 * contrato ou aditivo do Jurídico; no pedido e no título, só a leitura (`termsSummary`).
 */
export interface TermsForm {
  /** Vazio = sem condição estruturada (fica só o texto livre, como observação). */
  method: '' | PaymentMethod;
  netDays: string;
  installments: string;
  dueDay: string;
}

export const EMPTY_TERMS: TermsForm = { method: '', netDays: '', installments: '', dueDay: '' };

export function termsToForm(t: StructuredTerms | null | undefined): TermsForm {
  if (!t) return EMPTY_TERMS;
  return {
    method: t.method,
    netDays: String(t.netDays),
    installments: String(t.installments),
    dueDay: t.dueDay ? String(t.dueDay) : '',
  };
}

/** O que vai para a API: a condição completa, ou nada quando a forma não foi escolhida. */
export function formToTerms(f: TermsForm): StructuredTerms | null {
  if (!f.method) return null;
  return {
    method: f.method,
    netDays: f.netDays === '' ? Number.NaN : Number(f.netDays),
    installments: f.installments === '' ? 1 : Number(f.installments),
    dueDay: f.dueDay ? Number(f.dueDay) : null,
  };
}

/** "Boleto · 30 dias · 3 parcelas · vence todo dia 10". */
export function termsSummary(t: TFunction, terms: StructuredTerms | null | undefined): string {
  if (!terms) return '—';
  return [
    t(`partners.payment.methods.${terms.method}`),
    t('partners.payment.netDaysValue', { count: terms.netDays }),
    t('partners.payment.installmentsValue', { count: terms.installments }),
    terms.dueDay && t('partners.payment.dueDayValue', { day: terms.dueDay }),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PaymentTermsFields({
  idPrefix,
  value,
  onChange,
  disabled = false,
  error,
}: {
  idPrefix: string;
  value: TermsForm;
  onChange: (v: TermsForm) => void;
  disabled?: boolean;
  error?: (path: string) => string | undefined;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<TermsForm>) => onChange({ ...value, ...patch });
  const active = value.method !== '';
  return (
    <>
      <Field
        label={t('partners.payment.fields.method')}
        hint={t('paymentTerms.optionalHint')}
        error={error?.('terms.method')}
      >
        <Select
          id={`${idPrefix}-method`}
          value={value.method}
          disabled={disabled}
          onChange={(ev) => {
            const method = ev.target.value as TermsForm['method'];
            // Ao escolher a forma, prazo e parcelas começam com 30 dias e 1 parcela.
            set(
              method && !active
                ? {
                    method,
                    netDays: value.netDays || '30',
                    installments: value.installments || '1',
                  }
                : method
                  ? { method }
                  : EMPTY_TERMS,
            );
          }}
        >
          <option value="">{t('paymentTerms.none')}</option>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {t(`partners.payment.methods.${m}`)}
            </option>
          ))}
        </Select>
      </Field>
      {active && (
        <>
          <Field
            label={t('partners.payment.fields.netDays')}
            required
            error={error?.('terms.netDays')}
          >
            <Input
              id={`${idPrefix}-netDays`}
              type="number"
              min={0}
              max={365}
              required
              disabled={disabled}
              className="text-right tabular-nums"
              value={value.netDays}
              onChange={(ev) => set({ netDays: ev.target.value })}
            />
          </Field>
          <Field
            label={t('partners.payment.fields.installments')}
            required
            error={error?.('terms.installments')}
          >
            <Input
              id={`${idPrefix}-installments`}
              type="number"
              min={1}
              max={60}
              required
              disabled={disabled}
              className="text-right tabular-nums"
              value={value.installments}
              onChange={(ev) => set({ installments: ev.target.value })}
            />
          </Field>
          <Field
            label={t('partners.payment.fields.dueDay')}
            hint={t('partners.payment.fields.dueDayHint')}
            error={error?.('terms.dueDay')}
          >
            <Input
              id={`${idPrefix}-dueDay`}
              type="number"
              min={1}
              max={31}
              disabled={disabled}
              className="text-right tabular-nums"
              value={value.dueDay}
              onChange={(ev) => set({ dueDay: ev.target.value })}
            />
          </Field>
        </>
      )}
    </>
  );
}
